import { useEffect, useMemo, useRef, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { Toaster } from "sonner";
import { createAdminApi } from "./lib/api";
import { createAdminQueryClient } from "./lib/admin-query-client";
import { AuthProvider } from "./providers/AuthProvider";
import { useAuth } from "./providers/auth-context";
import { ApiContext } from "./providers/api-context";
import { router } from "./router";
import { ConfirmDialogHost } from "./components/confirm-dialog";

export function App() {
  const api = useMemo(() => createAdminApi(), []);
  const [queryClient] = useState(createAdminQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider api={api}>
        <ApiContext.Provider value={api}>
          <RouterBridge />
          <ConfirmDialogHost />
          <Toaster position="bottom-right" richColors closeButton />
        </ApiContext.Provider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export function RouterBridge() {
  const { session, loading: authLoading } = useAuth();
  const inviteNavigationInFlightRef = useRef<string | null>(null);
  const [inviteNavigation, setInviteNavigation] = useState<{
    sessionId: string | null;
    status: "idle" | "succeeded" | "failed";
  }>({ sessionId: null, status: "idle" });
  const pathname = router.state.location.pathname;
  const sessionId = session?.session?.id ?? session?.user.id ?? null;
  const requiresInvite = Boolean(
    !authLoading &&
    session?.user.status &&
    session.user.status !== "active" &&
    session.user.role !== "admin",
  );

  useEffect(() => {
    if (
      !requiresInvite ||
      pathname === "/auth/invite" ||
      !sessionId ||
      inviteNavigationInFlightRef.current === sessionId ||
      (inviteNavigation.sessionId === sessionId && inviteNavigation.status === "failed")
    ) {
      return;
    }

    inviteNavigationInFlightRef.current = sessionId;
    let cancelled = false;
    void router.navigate({ to: "/auth/invite" }).then(
      () => {
        if (cancelled) return;
        inviteNavigationInFlightRef.current = null;
        setInviteNavigation({
          sessionId,
          status: router.state.location.pathname === "/auth/invite" ? "succeeded" : "failed",
        });
      },
      () => {
        if (cancelled) return;
        inviteNavigationInFlightRef.current = null;
        setInviteNavigation({ sessionId, status: "failed" });
      },
    );
    return () => {
      cancelled = true;
      if (inviteNavigationInFlightRef.current === sessionId) {
        inviteNavigationInFlightRef.current = null;
      }
    };
  }, [inviteNavigation.sessionId, inviteNavigation.status, pathname, requiresInvite, sessionId]);

  useEffect(() => {
    if (authLoading || requiresInvite) return;
    void router.invalidate();
  }, [authLoading, requiresInvite, session?.user.id, session?.user.role, session?.user.status]);

  if (requiresInvite && pathname !== "/auth/invite") {
    const navigationFailed =
      inviteNavigation.sessionId === sessionId && inviteNavigation.status === "failed";
    return (
      <div
        role={navigationFailed ? "alert" : "status"}
        aria-live={navigationFailed ? "assertive" : "polite"}
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          fontFamily: "var(--f-sans)",
          color: "var(--ink-3)",
        }}
      >
        {navigationFailed ? (
          <>
            <span>招待コード入力画面へ移動できませんでした。</span>
            <button
              type="button"
              onClick={() => setInviteNavigation({ sessionId: null, status: "idle" })}
            >
              再試行
            </button>
          </>
        ) : (
          "招待コード入力画面へ移動中…"
        )}
      </div>
    );
  }

  return <RouterProvider router={router} context={{ session, authLoading }} />;
}
